import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ScienceIcon from "@mui/icons-material/Science";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ContractorProfileContent from "../../../../components/ContractorProfileContent";
import Loader from "../../../../components/Loader";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import servicesJSON from "../../../../inc/services.json";
import { formatLabel } from "../../../../libs/labels";

const RichTextEditor = lazy(() => import("../../../../components/RichTextEditor"));

const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

const normalizeProfileDoc = (id, data = {}) => ({
  id,
  ...data,
  connectAccountId: readField(data, "connect_account_id", ""),
  displayName: readField(data, "display_name", ""),
  workProfile: readField(data, "work_profile", {}),
  generatedResume: readField(data, "generated_resume", ""),
  generatedResumeHtml: readField(data, "generated_resume_html", ""),
  summary: readField(data, "summary", ""),
  experienceSummary: readField(data, "experience_summary", []),
  domainFocus: readField(data, "domain_focus", []),
  updatedAt: readField(data, "updated_at", null),
});

const statusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (["submitted"].includes(key)) return "warning";
  if (["shortlisted"].includes(key)) return "info";
  if (["matched"].includes(key)) return "secondary";
  if (["signed"].includes(key)) return "info";
  if (["active"].includes(key)) return "success";
  if (["closed"].includes(key)) return "default";
  return "default";
};

const statusLabel = (status) => String(status || "unknown").replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
const interviewStatusLabel = (status) => String(status || "not_scheduled").toLowerCase() === "completed"
  ? "Interviewed"
  : statusLabel(status || "not_scheduled");
const interviewStatusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (["completed", "scheduled"].includes(key)) return key === "completed" ? "success" : "info";
  if (["requested", "availability_sent"].includes(key)) return "warning";
  if (["declined", "no_show"].includes(key)) return "error";
  return "default";
};
const formatSlotDateTime = (value) => {
  if (!value) return "Time not set";
  const date = typeof value?.toDate === "function"
    ? value.toDate()
    : new Date(value?.seconds ? value.seconds * 1000 : value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "Time not set";
  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const dateValueMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value?.seconds || value?._seconds) return Number(value.seconds || value._seconds) * 1000;
  const millis = new Date(value).getTime();
  return Number.isFinite(millis) ? millis : 0;
};
const CONTRACT_STAGES = ["submitted", "shortlisted", "matched", "signed", "active", "closed"];
const HIRE_STEPS = ["Role family", "Specialty", "Scope of work", "Compensation", "Review"];

const normalizeContractStage = (status) => {
  const key = String(status || "").toLowerCase();
  if (["submitted"].includes(key)) return "submitted";
  if (["shortlisted"].includes(key)) return "shortlisted";
  if (["matched", "offer_sent", "contract_sent"].includes(key)) return "matched";
  if (["signed"].includes(key)) return "signed";
  if (["active"].includes(key)) return "active";
  if (["cancelled", "rejected", "closed", "failed", "uncollectible", "void"].includes(key)) return "closed";
  return "submitted";
};

const formatCurrency = (amountInCents, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format((amountInCents || 0) / 100);

const familyLabelByCode = {
  engineering: "Engineering",
  data_ai: "Data & AI",
};

const familyIconByCode = {
  engineering: EngineeringIcon,
  data_ai: ScienceIcon,
};

const roleIconByCode = {
  data_engineer: AnalyticsIcon,
  platform_engineer: ArchitectureIcon,
  cloud_engineer: EngineeringIcon,
  backend_engineer: CodeIcon,
  frontend_engineer: CodeIcon,
  analytics_engineer: AnalyticsIcon,
  ml_engineer: ScienceIcon,
  devops_engineer: EngineeringIcon,
};

const OPTION_CARD_SIZES = {
  compact: { padding: 1.25, minHeight: 96, avatar: 24, icon: 16, spacing: 1 },
  regular: { padding: 1.5, minHeight: 112, avatar: 30, icon: 22, spacing: 1.25 },
};

const OptionCard = ({
  selected,
  onClick,
  icon,
  title,
  description,
  vertical = false,
  square = true,
  size = "compact",
  maxHeight,
  maxWidth,
  iconRailWidth = 58,
}) => {
  const hasIcon = !!icon;
  const titleVariant = hasIcon ? "subtitle2" : "subtitle1";
  const dims = OPTION_CARD_SIZES[size] || OPTION_CARD_SIZES.compact;
  const iconNode = !hasIcon
    ? null
    : typeof icon === "string"
    ? <Box component="img" src={icon} alt="" sx={{ width: dims.icon, height: dims.icon }} />
    : icon;

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        width: "100%",
        ...(maxWidth ? { maxWidth, justifySelf: "start" } : {}),
        p: dims.padding,
        borderRadius: 1.5,
        cursor: "pointer",
        borderColor: selected ? "primary.main" : "divider",
        backgroundColor: selected ? "rgba(25, 118, 210, 0.06)" : "background.paper",
        boxShadow: selected ? "0 10px 28px rgba(25, 118, 210, 0.12)" : "none",
        transition: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
        ...(square ? { minHeight: dims.minHeight, aspectRatio: "1 / 1" } : {}),
        ...(maxHeight ? { maxHeight } : {}),
        "&:hover": {
          borderColor: "primary.light",
          backgroundColor: selected ? "rgba(25, 118, 210, 0.08)" : "#f8fafc",
          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack
        direction={vertical ? "column" : "row"}
        spacing={dims.spacing}
        alignItems={vertical ? "center" : "stretch"}
        justifyContent={vertical ? "center" : "flex-start"}
        sx={{ height: "100%", textAlign: vertical ? "center" : "left" }}
      >
        {hasIcon ? (
          vertical ? (
            <Avatar variant="rounded" sx={{ width: dims.avatar, height: dims.avatar, bgcolor: "grey.200", color: "grey.700" }}>
              {iconNode}
            </Avatar>
          ) : (
            <Box
              sx={{
                width: iconRailWidth,
                minWidth: iconRailWidth,
                borderRadius: 1.25,
                bgcolor: selected ? "primary.main" : "rgba(25, 118, 210, 0.08)",
                color: selected ? "primary.contrastText" : "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 0.25,
              }}
            >
              {iconNode}
            </Box>
          )
        ) : null}
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Typography variant={titleVariant} sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
};

const emptyHireForm = {
  roleFamily: "",
  roleCode: "",
  requirements: "",
  requirementsHtml: "",
  requirementsFormat: "html",
  roleLevel: "",
  projectTermMonths: "",
  desiredStartDate: "",
  pricingModel: "hourly",
  hourlyBudget: "",
  monthlyBudget: "",
  acceptTerms: false,
};

const toPlainText = (html = "") => {
  const value = String(html || "");
  if (typeof window !== "undefined" && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(value, "text/html");
    return String(doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const Contracts = ({
  embedded = false,
  loading,
  contracts,
  rateChangeRequests = [],
  scheduledRateChanges = [],
  rateChangeAuditRequests = [],
  busyRateRequestIds = {},
  onGenerateMatches,
  onRequestInterview,
  onSelectInterviewSlot,
  onResolveRateChange,
  onSubmitPosition,
  onRecordInterviewOutcome,
  onSelectContractor,
  onUpdateContract,
  onWithdrawContract,
  saving,
}) => {
  const [contractPage, setContractPage] = useState(0);
  const [contractRowsPerPage, setContractRowsPerPage] = useState(5);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedCandidateProfile, setSelectedCandidateProfile] = useState(null);
  const [candidateProfilesByUid, setCandidateProfilesByUid] = useState({});
  const [candidateProfilesLoading, setCandidateProfilesLoading] = useState(false);
  const [editingRequirements, setEditingRequirements] = useState(false);
  const [requirementsHtmlDraft, setRequirementsHtmlDraft] = useState("");
  const [requirementsPlainDraft, setRequirementsPlainDraft] = useState("");
  const [savingRequirements] = useState(false);
  const [rateActionNotice, setRateActionNotice] = useState({ type: "", text: "" });
  const [pricingHistory, setPricingHistory] = useState([]);
  const [pricingHistoryLoading, setPricingHistoryLoading] = useState(false);
  const [matchingContractId, setMatchingContractId] = useState("");
  const [matchNotice, setMatchNotice] = useState({ type: "", text: "" });
  const [interviewDialogCandidate, setInterviewDialogCandidate] = useState(null);
  const [interviewMessage, setInterviewMessage] = useState("");
  const [interviewSubmitting, setInterviewSubmitting] = useState(false);
  const [interviewSlotSubmitting, setInterviewSlotSubmitting] = useState("");

  const [hireDrawerOpen, setHireDrawerOpen] = useState(false);
  const [hireMode, setHireMode] = useState("create");
  const [editingContract, setEditingContract] = useState(null);
  const [editResetConfirmOpen, setEditResetConfirmOpen] = useState(false);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [candidateActionBusy, setCandidateActionBusy] = useState("");
  const [hireStep, setHireStep] = useState(0);
  const [hireForm, setHireForm] = useState(emptyHireForm);
  const [submittingHireRequest, setSubmittingHireRequest] = useState(false);

  const services = useMemo(() => servicesJSON.jobs || [], []);
  const roleFamilies = useMemo(
    () =>
      Array.from(new Set(services.map((s) => String(s.family || "").trim()).filter(Boolean))).map((code) => ({
        code,
        label: familyLabelByCode[code] || code.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase()),
      })),
    [services]
  );

  const specialties = useMemo(
    () => services.filter((s) => !hireForm.roleFamily || String(s.family || "") === String(hireForm.roleFamily || "")),
    [services, hireForm.roleFamily]
  );

  const selectedRole = useMemo(
    () => services.find((s) => s.code === hireForm.roleCode) || null,
    [services, hireForm.roleCode]
  );

  const roleLevels = useMemo(() => selectedRole?.levels || [], [selectedRole]);

  const selectedLevel = useMemo(
    () => roleLevels.find((l) => l.code === hireForm.roleLevel) || null,
    [roleLevels, hireForm.roleLevel]
  );

  const suggestedHourly = useMemo(() => {
    if (!selectedLevel) return 0;
    return Number(((Number(selectedLevel.minPrice || 0) + Number(selectedLevel.maxPrice || 0)) / 2).toFixed(2));
  }, [selectedLevel]);

  const suggestedMonthly = useMemo(() => Number((suggestedHourly * 160).toFixed(2)), [suggestedHourly]);

  useEffect(() => {
    if (!selectedRole) return;
    setHireForm((prev) => {
      const currentValid = roleLevels.some((level) => level.code === prev.roleLevel);
      return currentValid ? prev : { ...prev, roleLevel: roleLevels[0]?.code || "" };
    });
  }, [selectedRole, roleLevels]);

  useEffect(() => {
    if (hireForm.pricingModel === "hourly") {
      if (!hireForm.hourlyBudget && suggestedHourly > 0) {
        setHireForm((prev) => ({ ...prev, hourlyBudget: String(suggestedHourly) }));
      }
      return;
    }
    if (!hireForm.monthlyBudget && suggestedMonthly > 0) {
      setHireForm((prev) => ({ ...prev, monthlyBudget: String(suggestedMonthly) }));
    }
  }, [hireForm.pricingModel, suggestedHourly, suggestedMonthly, hireForm.hourlyBudget, hireForm.monthlyBudget]);

  const sortedContracts = useMemo(() => {
    const rows = [...contracts].sort((a, b) => {
      const aSec = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
      const bSec = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
      return bSec - aSec;
    });
    return rows;
  }, [contracts]);

  const pagedContracts = sortedContracts.slice(
    contractPage * contractRowsPerPage,
    contractPage * contractRowsPerPage + contractRowsPerPage
  );

  const minHourly = Number(selectedLevel?.minPrice || 0);
  const maxHourly = Number(selectedLevel?.maxPrice || 0);
  const minMonthlyFromHourly = Number((minHourly * 160).toFixed(2));
  const maxMonthlyFromHourly = Number((maxHourly * 160).toFixed(2));
  const hourlyValue = Number(hireForm.hourlyBudget || 0);
  const monthlyValue = Number(hireForm.monthlyBudget || 0);
  const hourlyTooLow = hireForm.pricingModel === "hourly" && hourlyValue > 0 && hourlyValue < minHourly;
  const hourlyTooHigh = hireForm.pricingModel === "hourly" && hourlyValue > 0 && maxHourly > 0 && hourlyValue > maxHourly;
  const fixedMonthlyTooLow = hireForm.pricingModel === "fixed_monthly" && monthlyValue > 0 && monthlyValue < minMonthlyFromHourly;
  const fixedMonthlyTooHigh = hireForm.pricingModel === "fixed_monthly" && monthlyValue > 0 && maxMonthlyFromHourly > 0 && monthlyValue > maxMonthlyFromHourly;
  const canProceedFamily = !!hireForm.roleFamily;
  const canProceedSpecialty = !!hireForm.roleCode && !!hireForm.roleLevel;
  const canProceedScope = !!hireForm.requirements.trim();
  const canProceedCompensation = hireForm.pricingModel === "hourly"
    ? hourlyValue > 0 && !hourlyTooLow && !hourlyTooHigh
    : monthlyValue > 0 && !fixedMonthlyTooLow && !fixedMonthlyTooHigh;
  const canSubmitHire = canProceedScope && canProceedCompensation && (hireMode === "edit" || !!hireForm.acceptTerms);
  const equivalentMonthlyFromHourly = hourlyValue > 0 ? hourlyValue * 160 : 0;
  const equivalentHourlyFromMonthly = monthlyValue > 0 ? monthlyValue / 160 : 0;
  const hireSubmitting = submittingHireRequest || (saving && hireStep === HIRE_STEPS.length - 1);

  const openHireDrawer = () => {
    setHireMode("create");
    setEditingContract(null);
    setHireForm(emptyHireForm);
    setHireDrawerOpen(true);
    setHireStep(0);
  };

  const toDateInput = (value) => {
    if (!value) return "";
    const date = typeof value?.toDate === "function"
      ? value.toDate()
      : new Date(Number(value?.seconds || value?._seconds || 0) * 1000 || value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : "";
  };

  const openEditDrawer = (contract) => {
    if (!contract || String(contract.status || "").toLowerCase() !== "submitted") return;
    const requirementsHtml = String(contract.requirementsHtml || "").trim();
    const requirements = String(contract.requirements || "").trim();
    setHireMode("edit");
    setEditingContract(contract);
    setHireForm({
      roleFamily: contract.roleFamily || "",
      roleCode: contract.roleCode || contract.roleSpecialty || "",
      requirements,
      requirementsHtml: requirementsHtml || (requirements ? `<p>${requirements}</p>` : ""),
      requirementsFormat: requirementsHtml ? "html" : "plain",
      roleLevel: contract.roleLevel || contract.roleSeniority || "",
      projectTermMonths: contract.projectTermMonths || "",
      desiredStartDate: toDateInput(contract.desiredStartDate),
      pricingModel: contract.pricingModel || "hourly",
      hourlyBudget: Number(contract.hourlyBudgetCents || 0) > 0 ? String(Number(contract.hourlyBudgetCents) / 100) : "",
      monthlyBudget: Number(contract.monthlyBudgetCents || 0) > 0 ? String(Number(contract.monthlyBudgetCents) / 100) : "",
      acceptTerms: true,
    });
    setHireDrawerOpen(true);
    setHireStep(0);
  };

  const closeHireDrawer = () => {
    if (hireSubmitting) return;
    setHireDrawerOpen(false);
    setHireStep(0);
    setHireForm(emptyHireForm);
    setEditingContract(null);
    setHireMode("create");
  };

  const handleSubmit = async (confirmedReset = false) => {
    const submitHandler = hireMode === "edit" ? onUpdateContract : onSubmitPosition;
    if (submittingHireRequest || typeof submitHandler !== "function") return;
    const existingCandidates = Array.isArray(editingContract?.candidateContractors) ? editingContract.candidateContractors : [];
    if (hireMode === "edit" && existingCandidates.length > 0 && !confirmedReset) {
      setEditResetConfirmOpen(true);
      return;
    }

    setSubmittingHireRequest(true);
    try {
      const payload = {
        ...hireForm,
        requirements: String(hireForm.requirements || "").trim(),
        requirementsHtml: String(hireForm.requirementsHtml || "").trim(),
        requirementsFormat: hireForm.requirementsFormat || "plain",
        suggestedHourly,
        suggestedMonthly,
        roleLabel: selectedRole?.label || "",
        roleTitle: selectedRole?.label || "",
        roleLevelLabel: selectedLevel?.label || "",
        roleFamily: String(hireForm.roleFamily || selectedRole?.family || ""),
        roleSpecialty: String(hireForm.roleCode || ""),
        roleSeniority: String(hireForm.roleLevel || ""),
        currency: selectedLevel?.currency || "usd",
      };
      if (hireMode === "edit") {
        await onUpdateContract(editingContract.id, { ...payload, expectedVersion: Number(editingContract.version || 0) });
      } else {
        await onSubmitPosition(payload);
      }
      setHireDrawerOpen(false);
      setHireStep(0);
      setHireForm(emptyHireForm);
      setEditingContract(null);
      setHireMode("create");
      setContractPage(0);
    } finally {
      setSubmittingHireRequest(false);
    }
  };

  const getContractDate = (contract) => {
    const sec = contract?.createdAt?.seconds || contract?.created_at?.seconds || 0;
    if (!sec) return "—";
    return new Date(sec * 1000).toLocaleDateString();
  };
  const getDesiredStartDate = (contract) => {
    const value = contract?.desiredStartDate;
    if (!value) return "Flexible";
    const date = typeof value?.toDate === "function" ? value.toDate() : new Date(Number(value?.seconds || value?._seconds || 0) * 1000 || value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : "Flexible";
  };

  const getBillingType = (contract) =>
    contract?.pricingModel === "fixed_monthly" ? "Fixed monthly" : "Hourly";

  const getRate = (contract) =>
    contract?.pricingModel === "fixed_monthly"
      ? `${formatCurrency(contract?.monthlyBudgetCents || 0, contract?.currency || "usd")}/mo`
      : `${formatCurrency(contract?.hourlyBudgetCents || 0, contract?.currency || "usd")}/hr`;

  const selectedStage = normalizeContractStage(selectedContract?.status);
  const selectedContractStatusKey = String(selectedContract?.status || "").toLowerCase();
  const selectedContractId = String(selectedContract?.id || "");
  const activeStageIndex = Math.max(CONTRACT_STAGES.indexOf(selectedStage), 0);
  const canEditRequirements = selectedStage === "submitted";
  const selectedDetailCandidates = useMemo(() => {
    const rows = Array.isArray(selectedContract?.candidateContractors)
      ? selectedContract.candidateContractors
      : [];
    if (selectedContractStatusKey !== "active") return rows;
    const linkedUid = String(selectedContract?.contractorUid || selectedContract?.matchedContractorUid || "").trim();
    const activeRows = rows.filter((candidate) => {
      const uid = String(candidate?.uid || "").trim();
      return Boolean(candidate?.selected) || (!!linkedUid && uid === linkedUid);
    });
    return activeRows.length > 0 ? activeRows.slice(0, 1) : rows.slice(0, 1);
  }, [selectedContract, selectedContractStatusKey]);
  const hydratedDetailCandidates = useMemo(
    () =>
      selectedDetailCandidates.map((candidate) => ({
        ...candidate,
        generatedProfile: candidateProfilesByUid[String(candidate?.uid || "").trim()] || null,
      })),
    [candidateProfilesByUid, selectedDetailCandidates]
  );
  const candidateSectionTitle = selectedContractStatusKey === "active" ? "Contractor" : "Shortlisted Candidates";
  const canGenerateMatches =
    !!selectedContract &&
    typeof onGenerateMatches === "function" &&
    ["submitted", "shortlisted"].includes(selectedContractStatusKey);
  const isMatchingSelected = matchingContractId === String(selectedContract?.id || "");
  const pendingRateChangesByContract = useMemo(() => {
    const map = {};
    (Array.isArray(rateChangeRequests) ? rateChangeRequests : []).forEach((row) => {
      if (String(row?.status || "").toLowerCase() !== "pending_client_approval") return;
      const contractId = String(row?.contractId || "").trim();
      if (!contractId) return;
      if (!map[contractId]) map[contractId] = [];
      map[contractId].push(row);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => {
        const aSec = Number(a.updatedAt?.seconds || a.createdAt?.seconds || 0);
        const bSec = Number(b.updatedAt?.seconds || b.createdAt?.seconds || 0);
        return bSec - aSec;
      });
    });
    return map;
  }, [rateChangeRequests]);
  const selectedContractRateRequests = useMemo(
    () => pendingRateChangesByContract[String(selectedContract?.id || "").trim()] || [],
    [pendingRateChangesByContract, selectedContract]
  );
  const selectedContractUpcomingRateChange = useMemo(() => {
    const contractId = String(selectedContract?.id || "").trim();
    if (!contractId) return null;
    const rows = (Array.isArray(scheduledRateChanges) ? scheduledRateChanges : [])
      .filter((row) => String(row?.contractId || "").trim() === contractId)
      .sort((a, b) => {
        const aSec = Number(a?.effectiveFrom?.seconds || a?.effectiveFrom?._seconds || 0);
        const bSec = Number(b?.effectiveFrom?.seconds || b?.effectiveFrom?._seconds || 0);
        return aSec - bSec;
      });
    return rows[0] || null;
  }, [scheduledRateChanges, selectedContract]);

  useEffect(() => {
    if (!selectedContractId) return;
    const fresh = (Array.isArray(contracts) ? contracts : []).find(
      (row) => String(row?.id || "") === selectedContractId
    );
    if (fresh) {
      setSelectedContract((prev) => (
        prev && String(prev?.id || "") === selectedContractId && fresh !== prev ? fresh : prev
      ));
    }
  }, [contracts, selectedContractId]);

  useEffect(() => {
    if (!selectedContract) {
      setEditingRequirements(false);
      setRequirementsHtmlDraft("");
      setRequirementsPlainDraft("");
      setRateActionNotice({ type: "", text: "" });
      setMatchNotice({ type: "", text: "" });
      setPricingHistory([]);
      setPricingHistoryLoading(false);
      setCandidateProfilesByUid({});
      setCandidateProfilesLoading(false);
      setInterviewDialogCandidate(null);
      setInterviewMessage("");
      return;
    }
    const initialHtml = String(selectedContract.requirementsHtml || "").trim();
    const fallbackPlain = String(selectedContract.requirements || "").trim();
    if (initialHtml) {
      setRequirementsHtmlDraft(initialHtml);
      setRequirementsPlainDraft(toPlainText(initialHtml));
    } else {
      setRequirementsHtmlDraft(fallbackPlain ? `<p>${fallbackPlain}</p>` : "");
      setRequirementsPlainDraft(fallbackPlain);
    }
    setEditingRequirements(false);
    setRateActionNotice({ type: "", text: "" });
  }, [selectedContract]);

  useEffect(() => {
    const candidateUids = selectedDetailCandidates
      .map((candidate) => String(candidate?.uid || "").trim())
      .filter(Boolean);

    if (!selectedContract?.id || !candidateUids.length) {
      setCandidateProfilesByUid({});
      setCandidateProfilesLoading(false);
      return;
    }

    let mounted = true;
    const loadCandidateProfiles = async () => {
      setCandidateProfilesLoading(true);
      try {
        const getCandidateProfiles = FirebaseAuth.functions().httpsCallable("getClientCandidateProfiles");
        const result = await getCandidateProfiles({
          contract_id: selectedContract?.id,
          candidate_uids: candidateUids,
        });
        const responseProfiles = Array.isArray(result?.data?.profiles) ? result.data.profiles : [];
        const entries = responseProfiles.map((row) => {
          const uid = String(row?.uid || "").trim();
          const profile = row?.profile
            ? normalizeProfileDoc(uid, row.profile)
            : null;
          return [uid, profile];
        });

        if (!mounted) return;
        setCandidateProfilesByUid(
          entries.reduce((acc, [uid, profile]) => {
            if (uid && profile) acc[uid] = profile;
            return acc;
          }, {})
        );
      } catch (_error) {
        if (mounted) setCandidateProfilesByUid({});
      } finally {
        if (mounted) setCandidateProfilesLoading(false);
      }
    };

    loadCandidateProfiles();
    return () => {
      mounted = false;
    };
  }, [selectedContract?.id, selectedDetailCandidates]);

  useEffect(() => {
    if (!selectedContract?.id) return;
    let mounted = true;
    const loadPricingHistory = async () => {
      setPricingHistoryLoading(true);
      try {
        const rows = (Array.isArray(rateChangeAuditRequests) ? rateChangeAuditRequests : [])
          .filter((row) => String(row?.contractId || "").trim() === String(selectedContract.id || "").trim())
          .filter((row) => String(row?.type || "").toLowerCase() === "contract_rate_change" && String(row?.status || "").toLowerCase() === "applied")
          .map((row) => ({
            id: `req-${row.id}`,
            type: "billing_rate_change_applied",
            requestId: String(row.id || ""),
            fromHourlyCents: Number(row.fromHourlyCents || 0),
            fromMonthlyCents: Number(row.fromMonthlyCents || 0),
            toHourlyCents: Number(row.requestedHourlyCents || 0),
            toMonthlyCents: Number(row.requestedMonthlyCents || 0),
            effectiveDate: String(row.effectiveDate || ""),
            effectiveFrom: row.effectiveFrom || null,
            createdAt: row.appliedAt || row.updatedAt || row.createdAt || null,
            source: "client_audit_request",
          }))
          .sort((a, b) => {
            const aTs = Number(a.createdAt?.seconds || a.createdAt?._seconds || 0);
            const bTs = Number(b.createdAt?.seconds || b.createdAt?._seconds || 0);
            return bTs - aTs;
          });
        if (mounted) setPricingHistory(rows);
      } finally {
        if (mounted) setPricingHistoryLoading(false);
      }
    };
    loadPricingHistory();
    return () => {
      mounted = false;
    };
  }, [selectedContract?.id, rateChangeAuditRequests]);

  const handleResolveRateChange = async (reqId, decision, effectiveDate, currentRateLabel = "", requestedRateLabel = "") => {
    if (typeof onResolveRateChange !== "function") return;
    setRateActionNotice({ type: "", text: "" });
    const result = await onResolveRateChange(reqId, decision, "");
    if (result?.ok) {
      if (decision === "approved") {
        setRateActionNotice({
          type: "info",
          text: `Pricing will change from ${currentRateLabel || "current rate"} to ${requestedRateLabel || "requested rate"} on the effective date${effectiveDate ? ` (${effectiveDate})` : ""}.`,
        });
      } else {
        setRateActionNotice({ type: "success", text: "Rate change request rejected." });
      }
      return;
    }
    if (result?.message) {
      setRateActionNotice({ type: "error", text: String(result.message) });
    }
  };

  const handleSaveRequirements = async () => {
    if (selectedContract && canEditRequirements) openEditDrawer(selectedContract);
  };

  const handleGenerateMatches = async () => {
    if (!selectedContract || typeof onGenerateMatches !== "function") return;
    const contractId = String(selectedContract.id || "").trim();
    if (!contractId || matchingContractId) return;

    setMatchNotice({ type: "", text: "" });
    setMatchingContractId(contractId);
    try {
      const result = await onGenerateMatches(contractId, Number(selectedContract.version || 0));
      setMatchNotice({
        type: result?.ok ? "success" : "error",
        text: result?.message || (result?.ok ? "Contractors matched for this request." : "Could not find contractors right now."),
      });
    } finally {
      setMatchingContractId("");
    }
  };

  const canRequestCandidateInterview = (candidate) => {
    if (!selectedContract || selectedContractStatusKey === "active" || typeof onRequestInterview !== "function") return false;
    const decision = String(candidate?.contractorDecision || "").toLowerCase();
    const interviewStatus = String(candidate?.interviewStatus || "").toLowerCase();
    return decision === "accepted" && !["requested", "availability_sent", "scheduled", "completed", "no_show"].includes(interviewStatus);
  };

  const handleRequestInterview = async () => {
    if (!selectedContract || !interviewDialogCandidate || typeof onRequestInterview !== "function") return;
    const contractId = String(selectedContract.id || "").trim();
    const candidateUid = String(interviewDialogCandidate.uid || "").trim();
    if (!contractId || !candidateUid || interviewSubmitting) return;

    setInterviewSubmitting(true);
    try {
      const result = await onRequestInterview(
        contractId,
        candidateUid,
        interviewMessage,
        Number(selectedContract.version || 0)
      );
      if (result?.ok) {
        setInterviewDialogCandidate(null);
        setInterviewMessage("");
        setMatchNotice({ type: "success", text: result.message || "Interview request sent to the contractor." });
      } else {
        setMatchNotice({ type: "error", text: result?.message || "Could not request an interview right now." });
      }
    } finally {
      setInterviewSubmitting(false);
    }
  };

  const handleSelectInterviewSlot = async (candidate, slotIndex) => {
    if (!selectedContract || !candidate || typeof onSelectInterviewSlot !== "function") return;
    const contractId = String(selectedContract.id || "").trim();
    const candidateUid = String(candidate.uid || "").trim();
    const key = `${candidateUid}:${slotIndex}`;
    if (!contractId || !candidateUid || interviewSlotSubmitting) return;

    setInterviewSlotSubmitting(key);
    try {
      const result = await onSelectInterviewSlot(
        contractId,
        candidateUid,
        slotIndex,
        Number(selectedContract.version || 0)
      );
      if (result?.ok) {
        setSelectedCandidateProfile(null);
        setMatchNotice({ type: "success", text: result.message || "Interview time selected." });
      } else {
        setMatchNotice({ type: "error", text: result?.message || "Could not select this interview time." });
      }
    } finally {
      setInterviewSlotSubmitting("");
    }
  };

  const handleCandidateSelection = async (candidate) => {
    if (!selectedContract || !candidate || typeof onSelectContractor !== "function") return;
    const candidateUid = String(candidate.uid || "").trim();
    if (!candidateUid || candidateActionBusy) return;
    setCandidateActionBusy(`select:${candidateUid}`);
    try {
      const result = await onSelectContractor(
        selectedContract.id,
        candidateUid,
        Number(selectedContract.version || 0)
      );
      setMatchNotice({ type: result?.ok ? "success" : "error", text: result?.message || "Could not select this contractor." });
      if (result?.ok) setSelectedCandidateProfile(null);
    } finally {
      setCandidateActionBusy("");
    }
  };

  const handleInterviewOutcome = async (candidate, outcome) => {
    if (!selectedContract || !candidate || typeof onRecordInterviewOutcome !== "function") return;
    const candidateUid = String(candidate.uid || "").trim();
    if (!candidateUid || candidateActionBusy) return;
    setCandidateActionBusy(`${outcome}:${candidateUid}`);
    try {
      const result = await onRecordInterviewOutcome(
        selectedContract.id,
        candidateUid,
        outcome,
        Number(selectedContract.version || 0)
      );
      setMatchNotice({ type: result?.ok ? "success" : "error", text: result?.message || "Could not record the interview outcome." });
      if (result?.ok) setSelectedCandidateProfile(null);
    } finally {
      setCandidateActionBusy("");
    }
  };

  const pricingHistoryCard = selectedContract ? (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        Pricing History
      </Typography>
      <Divider sx={{ my: 1.25 }} />
      {pricingHistoryLoading ? (
        <Typography variant="caption" color="text.secondary">Loading pricing history...</Typography>
      ) : pricingHistory.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No pricing changes recorded yet.</Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {pricingHistory.map((entry) => {
            const isMonthly = Number(entry?.toMonthlyCents || 0) > 0 || selectedContract?.pricingModel === "fixed_monthly";
            const rateLabel = isMonthly
              ? `${formatCurrency(Number(entry?.toMonthlyCents || 0), selectedContract?.currency || "usd")}/mo`
              : `${formatCurrency(Number(entry?.toHourlyCents || 0), selectedContract?.currency || "usd")}/hr`;
            const effective = String(entry?.effectiveDate || "").trim();
            const createdSec = Number(entry?.createdAt?.seconds || 0);
            const createdLabel = createdSec > 0 ? new Date(createdSec * 1000).toLocaleDateString() : "—";
            return (
              <Box key={entry.id} sx={{ py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{rateLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {effective ? `Effective ${effective}` : `Recorded ${createdLabel}`}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  ) : null;

  const Container = embedded ? Box : Paper;
  const containerProps = embedded ? {} : { variant: "outlined" };
  const containerSx = embedded ? { p: 0 } : { p: 2, mb: 2, borderRadius: "8px" };

  return (
    <Container {...containerProps} sx={containerSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{ width: 36, height: 36, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}
          >
            <AssignmentIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Engagements</Typography>
            <Typography variant="body2" color="text.secondary">
              Track requests, active work, and contractor details.
            </Typography>
          </Box>
        </Stack>
        <Button variant="outlined" size="small" startIcon={<AddCircleIcon />} onClick={openHireDrawer} sx={{ textTransform: "none" }}>
          Request hire
        </Button>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : (
        <>
          {pagedContracts.length === 0 ? (
            <Box sx={{ flexGrow: 1, minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" color="text.secondary">No engagements found.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ flexGrow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Billing Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedContracts.map((c) => {
                    const stage = normalizeContractStage(c.status);
                    return (
                      <TableRow
                        key={c.id}
                        hover
                        onClick={() => setSelectedContract(c)}
                        selected={String(selectedContract?.id || "") === String(c.id || "")}
                        sx={{
                          cursor: "pointer",
                          "&.Mui-selected": {
                            bgcolor: "rgba(25, 118, 210, 0.08)",
                          },
                          "&.Mui-selected:hover": {
                            bgcolor: "rgba(25, 118, 210, 0.12)",
                          },
                        }}
                      >
                        <TableCell>{getContractDate(c)}</TableCell>
                        <TableCell>{c.roleTitle || c.roleLabel || "—"}</TableCell>
                        <TableCell>{getBillingType(c)}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel(stage)}
                            color={statusColor(stage)}
                            sx={{ borderRadius: 1 }}
                          />
                        </TableCell>
                        <TableCell align="right">{getRate(c)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {sortedContracts.length > 0 && (
        <Box
          sx={{
            mt: "auto",
            pt: 0.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <TablePagination
            component="div"
            count={sortedContracts.length}
            page={contractPage}
            onPageChange={(_e, p) => setContractPage(p)}
            rowsPerPage={contractRowsPerPage}
            onRowsPerPageChange={(e) => {
              setContractRowsPerPage(parseInt(e.target.value, 10));
              setContractPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Show"
          />
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
        </Box>
      )}

      {selectedContract ? (
        <Paper
          variant="outlined"
          sx={{
            bgcolor: "grey.50",
            borderColor: "divider",
            borderRadius: 2,
            mt: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ bgcolor: "background.paper", borderBottom: (theme) => `1px solid ${theme.palette.divider}`, p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar variant="rounded" sx={{ bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                  <AssignmentIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {selectedContract.roleTitle || selectedContract.roleLabel || "Engagement details"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getBillingType(selectedContract)} • {getRate(selectedContract)}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {selectedContractStatusKey === "submitted" && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => openEditDrawer(selectedContract)} sx={{ textTransform: "none" }}>
                      Edit request
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => setWithdrawConfirmOpen(true)} sx={{ textTransform: "none" }}>
                      Withdraw
                    </Button>
                  </>
                )}
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedCandidateProfile(null);
                    setSelectedContract(null);
                  }}
                  aria-label="Close engagement details"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Engagement summary
                </Typography>
                <Divider sx={{ my: 1.25 }} />
                <Box
                  sx={{
                    display: "grid",
                    gap: { xs: 1.25, md: 2 },
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
                  }}
                >
                  {[
                    ["Request date", getContractDate(selectedContract)],
                    ["Desired start", getDesiredStartDate(selectedContract)],
                    ["Category", selectedContract.roleTitle || selectedContract.roleLabel || "—"],
                    ["Billing type", getBillingType(selectedContract)],
                    ["Rate", getRate(selectedContract)],
                  ].map(([label, value]) => (
                    <Box
                      key={label}
                      sx={{
                        minWidth: 0,
                        borderRight: { lg: (theme) => (label === "Rate" ? 0 : `1px solid ${theme.palette.divider}`) },
                        pr: { lg: label === "Rate" ? 0 : 2 },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.35, textTransform: "uppercase" }}>
                        {label}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 750, mt: 0.5 }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {selectedContractRateRequests.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: "background.paper" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Pending rate change approval
                  </Typography>
                  {!!rateActionNotice.text && (
                    <Alert severity={rateActionNotice.type || "info"} sx={{ mb: 1.25 }}>
                      {rateActionNotice.text}
                    </Alert>
                  )}
                  <Stack spacing={1}>
                    {selectedContractRateRequests.map((req) => {
                      const pricingModel = String(req.pricingModel || selectedContract.pricingModel || "").toLowerCase();
                      const isMonthly = pricingModel === "fixed_monthly";
                      const currentRateLabel = isMonthly
                        ? `${formatCurrency(Number(req.currentMonthlyCents || selectedContract.monthlyBudgetCents || 0), req.currency || selectedContract.currency || "usd")}/mo`
                        : `${formatCurrency(Number(req.currentHourlyCents || selectedContract.hourlyBudgetCents || 0), req.currency || selectedContract.currency || "usd")}/hr`;
                      const requestedRateLabel = isMonthly
                        ? `${formatCurrency(Number(req.requestedMonthlyCents || 0), req.currency || selectedContract.currency || "usd")}/mo`
                        : `${formatCurrency(Number(req.requestedHourlyCents || 0), req.currency || selectedContract.currency || "usd")}/hr`;
                      const busy = !!busyRateRequestIds[String(req.id || "")];
                      return (
                        <Paper key={req.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25 }}>
                          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                            <Box>
                              <Typography variant="body2">
                                Current <strong>{currentRateLabel}</strong> → Requested <strong>{requestedRateLabel}</strong>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Effective date: {String(req.effectiveDate || "—")}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={busy || typeof onResolveRateChange !== "function"}
                                startIcon={busy ? <Loader size={14} /> : null}
                                onClick={() =>
                                  handleResolveRateChange(
                                    req.id,
                                    "approved",
                                    String(req.effectiveDate || ""),
                                    currentRateLabel,
                                    requestedRateLabel
                                  )
                                }
                              >
                                {busy ? "Updating..." : "Approve"}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                disabled={busy || typeof onResolveRateChange !== "function"}
                                startIcon={busy ? <Loader size={14} /> : null}
                                onClick={() => handleResolveRateChange(req.id, "rejected", String(req.effectiveDate || ""))}
                              >
                                {busy ? "Updating..." : "Reject"}
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Paper>
              )}

              {!!selectedContractUpcomingRateChange && (
                <Alert severity="info">
                  Pricing is scheduled to update on the approved effective date.
                </Alert>
              )}

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Requirements
                  </Typography>
                  <Chip label={statusLabel(selectedStage)} color={statusColor(selectedStage)} size="small" sx={{ borderRadius: 1 }} />
                </Stack>
                <Divider sx={{ my: 1.25 }} />

                {editingRequirements && canEditRequirements ? (
                  <Stack spacing={1.25}>
                    <Suspense fallback={<Skeleton variant="rounded" height={224} />}>
                      <RichTextEditor
                        value={requirementsHtmlDraft}
                        minHeight={160}
                        placeholder="Describe role scope, deliverables, tools, and collaboration expectations..."
                        onChange={(html) => {
                          const sanitized = DOMPurify.sanitize(html || "");
                          setRequirementsHtmlDraft(sanitized);
                          setRequirementsPlainDraft(toPlainText(sanitized));
                        }}
                      />
                    </Suspense>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={handleSaveRequirements}
                        disabled={savingRequirements || !String(requirementsPlainDraft || "").trim()}
                        startIcon={savingRequirements ? <Loader size={14} /> : null}
                      >
                        {savingRequirements ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const initialHtml = String(selectedContract.requirementsHtml || "").trim();
                          const fallbackPlain = String(selectedContract.requirements || "").trim();
                          setRequirementsHtmlDraft(initialHtml || (fallbackPlain ? `<p>${fallbackPlain}</p>` : ""));
                          setRequirementsPlainDraft(initialHtml ? toPlainText(initialHtml) : fallbackPlain);
                          setEditingRequirements(false);
                        }}
                        disabled={savingRequirements}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : selectedContract.requirementsFormat === "html" && selectedContract.requirementsHtml ? (
                  <Box
                    sx={{
                      color: "text.primary",
                      lineHeight: 1.65,
                      maxHeight: 260,
                      overflowY: "auto",
                      "& p": { my: 1 },
                      "& ul, & ol": { pl: 3 },
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedContract.requirementsHtml) }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedContract.requirements || "—"}
                  </Typography>
                )}
              </Box>

              <Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {candidateSectionTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedContractStatusKey === "active"
                        ? "Contractor assigned to this engagement."
                        : "Review matched contractors and request interviews."}
                    </Typography>
                  </Box>
                  {canGenerateMatches && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={isMatchingSelected ? <Loader size={14} /> : <AutoAwesomeOutlinedIcon fontSize="small" />}
                      onClick={handleGenerateMatches}
                      disabled={isMatchingSelected}
                      sx={{ textTransform: "none" }}
                    >
                      {isMatchingSelected ? "Finding..." : selectedContractStatusKey === "shortlisted" ? "Find more contractors" : "Find contractors"}
                    </Button>
                  )}
                </Stack>
                <Divider sx={{ my: 1.25 }} />
                {!!matchNotice.text && (
                  <Alert severity={matchNotice.type || "info"} sx={{ mb: 1.25, borderRadius: 1.5 }}>
                    {matchNotice.text}
                  </Alert>
                )}
                {candidateProfilesLoading ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
                    {[0, 1].map((item) => (
                      <Paper key={item} variant="outlined" sx={{ p: 1.5, borderRadius: 1.25 }}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Skeleton variant="rounded" width={46} height={46} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton width="45%" height={24} />
                              <Skeleton width="32%" height={18} />
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={0.75}>
                            <Skeleton variant="rounded" width={92} height={24} />
                            <Skeleton variant="rounded" width={108} height={24} />
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                ) : hydratedDetailCandidates.length > 0 ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
                    {hydratedDetailCandidates.map((candidate) => {
                      const matchScore = Number(candidate?.matchScore || candidate?.score || candidate?.match || 0);
                      const decision = String(candidate?.contractorDecision || "pending");
                      const interviewStatus = String(candidate?.interviewStatus || "not_scheduled");
                      const generatedProfile = candidate?.generatedProfile || null;
                      const canRequestInterview = canRequestCandidateInterview(candidate);
                      return (
                        <Paper
                          key={candidate.uid}
                          variant="outlined"
                          onClick={() => setSelectedCandidateProfile(candidate)}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.25,
                            cursor: "pointer",
                            transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                            "&:hover": {
                              borderColor: "primary.light",
                              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.25}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Avatar variant="rounded" sx={{ width: 46, height: 46, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                                <DescriptionOutlinedIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                                  {candidate.displayName || "Contractor"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                  {[formatLabel(candidate.service), formatLabel(candidate.level)].filter(Boolean).join(" • ") || "Profile details"}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                              {matchScore > 0 && <Chip size="small" label={`Match ${matchScore}%`} sx={{ borderRadius: 1 }} />}
                              {!generatedProfile && <Chip size="small" label="Profile unavailable" sx={{ borderRadius: 1 }} />}
                              <Chip size="small" label={statusLabel(decision)} color={decision === "accepted" ? "success" : decision === "declined" ? "error" : "default"} sx={{ borderRadius: 1 }} />
                              <Chip size="small" label={interviewStatusLabel(interviewStatus)} color={interviewStatusColor(interviewStatus)} sx={{ borderRadius: 1 }} />
                              {canRequestInterview && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EventAvailableOutlinedIcon fontSize="small" />}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setInterviewDialogCandidate(candidate);
                                    setInterviewMessage("");
                                  }}
                                  sx={{ textTransform: "none", ml: { sm: 0.5 } }}
                                >
                                  Request interview
                                </Button>
                              )}
                              {decision === "accepted" && ["submitted", "shortlisted"].includes(selectedContractStatusKey) && typeof onSelectContractor === "function" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={(event) => { event.stopPropagation(); handleCandidateSelection(candidate); }}
                                  disabled={Boolean(candidateActionBusy)}
                                  sx={{ textTransform: "none" }}
                                >
                                  {candidateActionBusy === `select:${candidate.uid}` ? "Selecting..." : "Select contractor"}
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: "background.paper" }}>
                    <Typography variant="body2" sx={{ fontWeight: 750 }}>
                      {selectedContractStatusKey === "active"
                        ? "Contractor details are not available yet."
                        : "No contractors matched yet."}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedContractStatusKey === "active"
                        ? "The assigned contractor profile will appear here once available."
                        : "Use AI matching to find contractors for this request."}
                    </Typography>
                  </Paper>
                )}
              </Box>

              {pricingHistoryCard}
            </Stack>
          </Box>
        </Paper>
      ) : sortedContracts.length > 0 && !loading ? (
        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
          <Typography variant="body2" color="text.secondary">
            Select an engagement above to view requirements, contractor details, pricing history, and rate changes.
          </Typography>
        </Paper>
      ) : null}

      <Drawer
        anchor="bottom"
        open={false}
        onClose={() => {
          setSelectedCandidateProfile(null);
          setSelectedContract(null);
        }}
        PaperProps={{
          sx: {
            top: { xs: 96, sm: 104 },
            left: 0,
            right: 0,
            width: "100%",
            maxWidth: "100%",
            height: { xs: "calc(100dvh - 96px)", sm: "calc(100dvh - 104px)" },
            maxHeight: { xs: "calc(100dvh - 96px)", sm: "calc(100dvh - 104px)" },
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Hire Request Details
              </Typography>
              {!!selectedContract && (
                <Typography variant="body2" color="text.secondary">
                  {selectedContract.roleTitle || selectedContract.roleLabel || "Untitled role"} • {statusLabel(selectedContract.status || selectedStage)}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedCandidateProfile(null);
                setSelectedContract(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 }, overflowY: "auto", flex: 1, bgcolor: "grey.50" }}>
          {!!selectedContract && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
              <Box sx={{ minWidth: { md: 112 }, display: { xs: "none", md: "block" } }}>
                <Stepper activeStep={activeStageIndex} orientation="vertical">
                  {CONTRACT_STAGES.map((stage) => (
                    <Step key={stage}>
                      <StepLabel>{statusLabel(stage)}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Stack spacing={2} sx={{ flex: 1 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2">Role</Typography>
                      <Typography variant="body2" sx={{ maxWidth: "65%", textAlign: "right" }}>
                        {selectedContract.roleTitle || selectedContract.roleLabel || "Untitled role"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2">Date</Typography>
                      <Typography variant="body2" sx={{ maxWidth: "65%", textAlign: "right" }}>
                        {getContractDate(selectedContract)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2">Billing Type</Typography>
                      <Typography variant="body2" sx={{ maxWidth: "65%", textAlign: "right" }}>
                        {getBillingType(selectedContract)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2">Rate</Typography>
                      <Typography variant="body2" sx={{ maxWidth: "65%", textAlign: "right" }}>
                        {getRate(selectedContract)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>

                {selectedContractRateRequests.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Pending Rate Change Approval
                    </Typography>
                    {!!rateActionNotice.text && (
                      <Alert severity={rateActionNotice.type || "info"} sx={{ mb: 1.25 }}>
                        {rateActionNotice.text}
                      </Alert>
                    )}
                    <Stack spacing={1}>
                      {selectedContractRateRequests.map((req) => {
                        const pricingModel = String(req.pricingModel || selectedContract.pricingModel || "").toLowerCase();
                        const isMonthly = pricingModel === "fixed_monthly";
                        const currentRateLabel = isMonthly
                          ? `${formatCurrency(Number(req.currentMonthlyCents || selectedContract.monthlyBudgetCents || 0), req.currency || selectedContract.currency || "usd")}/mo`
                          : `${formatCurrency(Number(req.currentHourlyCents || selectedContract.hourlyBudgetCents || 0), req.currency || selectedContract.currency || "usd")}/hr`;
                        const requestedRateLabel = isMonthly
                          ? `${formatCurrency(Number(req.requestedMonthlyCents || 0), req.currency || selectedContract.currency || "usd")}/mo`
                          : `${formatCurrency(Number(req.requestedHourlyCents || 0), req.currency || selectedContract.currency || "usd")}/hr`;
                        const busy = !!busyRateRequestIds[String(req.id || "")];
                        return (
                          <Paper key={req.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25 }}>
                            <Stack spacing={0.75}>
                              <Typography variant="caption" color="text.secondary">
                                Effective date: {String(req.effectiveDate || "—")}
                              </Typography>
                              <Typography variant="body2">
                                Current: <strong>{currentRateLabel}</strong>
                              </Typography>
                              <Typography variant="body2">
                                Requested: <strong>{requestedRateLabel}</strong>
                              </Typography>
                              {req.reason ? (
                                <Typography variant="caption" color="text.secondary">
                                  Admin note: {String(req.reason)}
                                </Typography>
                              ) : null}
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={busy || typeof onResolveRateChange !== "function"}
                                  startIcon={busy ? <Loader size={14} /> : null}
                                  onClick={() =>
                                    handleResolveRateChange(
                                      req.id,
                                      "approved",
                                      String(req.effectiveDate || ""),
                                      currentRateLabel,
                                      requestedRateLabel
                                    )
                                  }
                                >
                                  {busy ? "Updating..." : "Approve"}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  disabled={busy || typeof onResolveRateChange !== "function"}
                                  startIcon={busy ? <Loader size={14} /> : null}
                                  onClick={() => handleResolveRateChange(req.id, "rejected", String(req.effectiveDate || ""))}
                                >
                                  {busy ? "Updating..." : "Reject"}
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Paper>
                )}

                {!!selectedContractUpcomingRateChange && (
                  <Alert severity="info">
                    {(() => {
                      const req = selectedContractUpcomingRateChange;
                      const pricingModel = String(req?.pricingModel || selectedContract?.pricingModel || "").toLowerCase();
                      const isMonthly = pricingModel === "fixed_monthly";
                      const nowSec = Math.floor(Date.now() / 1000);
                      const effectiveSec = Number(req?.effectiveFrom?.seconds || req?.effectiveFrom?._seconds || 0);
                      const isPendingApply = effectiveSec > 0 && effectiveSec <= nowSec;
                      const currentRateLabel = isMonthly
                        ? `${formatCurrency(Number(req?.currentMonthlyCents || selectedContract?.monthlyBudgetCents || 0), req?.currency || selectedContract?.currency || "usd")}/mo`
                        : `${formatCurrency(Number(req?.currentHourlyCents || selectedContract?.hourlyBudgetCents || 0), req?.currency || selectedContract?.currency || "usd")}/hr`;
                      const requestedRateLabel = isMonthly
                        ? `${formatCurrency(Number(req?.requestedMonthlyCents || 0), req?.currency || selectedContract?.currency || "usd")}/mo`
                        : `${formatCurrency(Number(req?.requestedHourlyCents || 0), req?.currency || selectedContract?.currency || "usd")}/hr`;
                      const effectiveDate = String(req?.effectiveDate || "").trim();
                      if (isPendingApply) {
                        return `Pricing change from ${currentRateLabel} to ${requestedRateLabel} is approved and pending application${effectiveDate ? ` (effective ${effectiveDate})` : ""}.`;
                      }
                      return `Pricing will change from ${currentRateLabel} to ${requestedRateLabel} on the effective date${effectiveDate ? ` (${effectiveDate})` : ""}.`;
                    })()}
                  </Alert>
                )}

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Requirements
                    </Typography>
                    <Chip label={statusLabel(selectedStage)} color={statusColor(selectedStage)} size="small" sx={{ borderRadius: 1 }} />
                  </Stack>

                  {editingRequirements && canEditRequirements ? (
                    <Stack spacing={1.25}>
                      <Suspense fallback={<Skeleton variant="rounded" height={224} />}>
                        <RichTextEditor
                          value={requirementsHtmlDraft}
                          minHeight={160}
                          placeholder="Describe role scope, deliverables, tools, and collaboration expectations..."
                          onChange={(html) => {
                            const sanitized = DOMPurify.sanitize(html || "");
                            setRequirementsHtmlDraft(sanitized);
                            setRequirementsPlainDraft(toPlainText(sanitized));
                          }}
                        />
                      </Suspense>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          onClick={handleSaveRequirements}
                          disabled={savingRequirements || !String(requirementsPlainDraft || "").trim()}
                          startIcon={savingRequirements ? <Loader size={14} /> : null}
                        >
                          {savingRequirements ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const initialHtml = String(selectedContract.requirementsHtml || "").trim();
                            const fallbackPlain = String(selectedContract.requirements || "").trim();
                            setRequirementsHtmlDraft(initialHtml || (fallbackPlain ? `<p>${fallbackPlain}</p>` : ""));
                            setRequirementsPlainDraft(initialHtml ? toPlainText(initialHtml) : fallbackPlain);
                            setEditingRequirements(false);
                          }}
                          disabled={savingRequirements}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  ) : selectedContract.requirementsFormat === "html" && selectedContract.requirementsHtml ? (
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: "grey.50",
                        maxHeight: 260,
                        overflowY: "auto",
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedContract.requirementsHtml) }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedContract.requirements || "—"}
                    </Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {candidateSectionTitle}
                  </Typography>
                  {candidateProfilesLoading ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
                      {[0, 1].map((item) => (
                        <Paper key={item} variant="outlined" sx={{ p: 1.5, borderRadius: 1.25 }}>
                          <Stack spacing={1.25}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Skeleton variant="rounded" width={46} height={46} />
                              <Box sx={{ flex: 1 }}>
                                <Skeleton width="45%" height={24} />
                                <Skeleton width="32%" height={18} />
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={0.75}>
                              <Skeleton variant="rounded" width={92} height={24} />
                              <Skeleton variant="rounded" width={108} height={24} />
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                  ) : hydratedDetailCandidates.length > 0 ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
                      {hydratedDetailCandidates.map((candidate) => {
                        const matchScore = Number(candidate?.matchScore || candidate?.score || candidate?.match || 0);
                        const decision = String(candidate?.contractorDecision || "pending");
                        const interviewStatus = String(candidate?.interviewStatus || "not_scheduled");
                        const generatedProfile = candidate?.generatedProfile || null;
                        const matchStrengths = Array.isArray(candidate?.matchStrengths) ? candidate.matchStrengths : [];
                        const matchGaps = Array.isArray(candidate?.matchGaps) ? candidate.matchGaps : [];
                        return (
                          <Paper
                            key={candidate.uid}
                            variant="outlined"
                            onClick={() => setSelectedCandidateProfile(candidate)}
                            sx={{
                              p: 1.5,
                              borderRadius: 1.25,
                              cursor: "pointer",
                              transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                              "&:hover": {
                                borderColor: "primary.light",
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <Avatar variant="rounded" sx={{ width: 46, height: 46, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                                  <DescriptionOutlinedIcon fontSize="small" />
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                    {candidate.displayName || "Contractor"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                    {[formatLabel(candidate.service), formatLabel(candidate.level)].filter(Boolean).join(" • ") || "Profile details"}
                                  </Typography>
                                </Box>
                              {(matchStrengths.length > 0 || matchGaps.length > 0) && (
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ display: { xs: "none", sm: "flex" } }}>
                                  {matchStrengths.slice(0, 2).map((item) => (
                                    <Chip key={`strength-${candidate.uid}-${item}`} size="small" label={item} color="success" variant="outlined" sx={{ borderRadius: 1 }} />
                                  ))}
                                  {matchGaps.slice(0, 1).map((item) => (
                                    <Chip key={`gap-${candidate.uid}-${item}`} size="small" label={item} color="warning" variant="outlined" sx={{ borderRadius: 1 }} />
                                  ))}
                                </Stack>
                              )}
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                                {matchScore > 0 && <Chip size="small" label={`Match ${matchScore}%`} sx={{ borderRadius: 1 }} />}
                                {!generatedProfile && <Chip size="small" label="Profile unavailable" sx={{ borderRadius: 1 }} />}
                                <Chip size="small" label={statusLabel(decision)} color={decision === "accepted" ? "success" : decision === "declined" ? "error" : "default"} sx={{ borderRadius: 1 }} />
                                <Chip size="small" label={statusLabel(interviewStatus)} color={interviewStatusColor(interviewStatus)} sx={{ borderRadius: 1 }} />
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {selectedContractStatusKey === "active"
                        ? "Contractor details are not available yet."
                        : "Admin has not published a shortlist yet."}
                    </Typography>
                  )}
                </Paper>

                {pricingHistoryCard}
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={!!selectedCandidateProfile}
        onClose={() => setSelectedCandidateProfile(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Candidate Profile
          <IconButton
            size="small"
            onClick={() => setSelectedCandidateProfile(null)}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close candidate profile"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!!selectedCandidateProfile && (
            (() => {
              const generatedProfile = selectedCandidateProfile.generatedProfile || null;
              const matchFeedback = String(selectedCandidateProfile.matchFeedback || "").trim();
              const matchStrengths = Array.isArray(selectedCandidateProfile.matchStrengths) ? selectedCandidateProfile.matchStrengths : [];
              const matchGaps = Array.isArray(selectedCandidateProfile.matchGaps) ? selectedCandidateProfile.matchGaps : [];
              const generatedText = String(
                generatedProfile?.generatedResume ||
                  generatedProfile?.summary ||
                  selectedCandidateProfile.profileSummary ||
                  selectedCandidateProfile.summary ||
                  ""
              ).trim();
              const matchScore = Number(selectedCandidateProfile.matchScore || selectedCandidateProfile.score || selectedCandidateProfile.match || 0);
              const candidateInterviewStatus = String(selectedCandidateProfile.interviewStatus || "").toLowerCase();
              const candidateTimeSlots = Array.isArray(selectedCandidateProfile.timeSlots) ? selectedCandidateProfile.timeSlots : [];
              const selectedTimeSlots = candidateTimeSlots.filter((slot) => Boolean(slot?.selected));
              const displayedTimeSlots = candidateInterviewStatus === "scheduled" && selectedTimeSlots.length > 0
                ? selectedTimeSlots
                : candidateTimeSlots;

              return (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                      <DescriptionOutlinedIcon />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {selectedCandidateProfile.displayName || generatedProfile?.displayName || "Contractor"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[formatLabel(selectedCandidateProfile.service), formatLabel(selectedCandidateProfile.level)].filter(Boolean).join(" • ") || "Candidate details"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {matchScore > 0 && (
                      <Chip
                        size="small"
                        label={`Match ${matchScore}%`}
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                    {!generatedProfile && <Chip size="small" label="Profile unavailable" sx={{ borderRadius: 1 }} />}
                    <Chip
                      size="small"
                      label={statusLabel(selectedCandidateProfile.contractorDecision || "pending")}
                      color={String(selectedCandidateProfile.contractorDecision || "").toLowerCase() === "accepted" ? "success" : "default"}
                      sx={{ borderRadius: 1 }}
                    />
                    <Chip
                      size="small"
                      label={interviewStatusLabel(selectedCandidateProfile.interviewStatus || "not_scheduled")}
                      color={interviewStatusColor(selectedCandidateProfile.interviewStatus)}
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>

                  {String(selectedCandidateProfile.contractorDecision || "").toLowerCase() === "accepted" && ["submitted", "shortlisted"].includes(selectedContractStatusKey) && (
                    <Button
                      variant="contained"
                      onClick={() => handleCandidateSelection(selectedCandidateProfile)}
                      disabled={Boolean(candidateActionBusy)}
                    >
                      {candidateActionBusy === `select:${selectedCandidateProfile.uid}` ? "Selecting..." : "Select this contractor"}
                    </Button>
                  )}

                  {String(selectedCandidateProfile.interviewStatus || "").toLowerCase() === "scheduled" &&
                    dateValueMillis(selectedCandidateProfile.interviewAt) > 0 &&
                    dateValueMillis(selectedCandidateProfile.interviewAt) <= Date.now() && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Record interview outcome
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleInterviewOutcome(selectedCandidateProfile, "completed")}
                          disabled={Boolean(candidateActionBusy)}
                        >
                          Interviewed
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleInterviewOutcome(selectedCandidateProfile, "no_show")}
                          disabled={Boolean(candidateActionBusy)}
                        >
                          No show
                        </Button>
                      </Stack>
                    </Paper>
                  )}

                  {(matchFeedback || matchStrengths.length > 0 || matchGaps.length > 0) && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.25, bgcolor: "rgba(25, 118, 210, 0.04)" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Why this candidate
                      </Typography>
                      {matchFeedback && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: matchStrengths.length || matchGaps.length ? 1.25 : 0 }}>
                          {matchFeedback}
                        </Typography>
                      )}
                      {matchStrengths.length > 0 && (
                        <Box sx={{ mb: matchGaps.length ? 1.25 : 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                            Strengths
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                            {matchStrengths.map((item) => (
                              <Box component="li" key={`strength-${item}`} sx={{ mb: 0.4 }}>
                                <Typography variant="body2">{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {matchGaps.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                            Things to validate
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                            {matchGaps.map((item) => (
                              <Box component="li" key={`gap-${item}`} sx={{ mb: 0.4 }}>
                                <Typography variant="body2">{item}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  )}

                  <ContractorProfileContent
                    profile={{
                      ...(generatedProfile || {}),
                      generatedResume: generatedProfile?.generatedResume || generatedText,
                    }}
                  />

                  {displayedTimeSlots.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.25 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ mb: 1.25 }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {candidateInterviewStatus === "scheduled" ? "Scheduled interview" : "Interview availability"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedCandidateProfile.timezone || "Timezone not specified"}
                          </Typography>
                        </Box>
                        {candidateInterviewStatus === "scheduled" && (
                          <Chip size="small" color="info" label="Scheduled" sx={{ borderRadius: 1 }} />
                        )}
                      </Stack>
                      <Stack spacing={1}>
                        {displayedTimeSlots.map((slot, index) => {
                          const isSelected = Boolean(slot.selected);
                          const startsAt = slot.startsAt || slot.starts_at || (isSelected ? selectedCandidateProfile.interviewAt : "");
                          const canSelectSlot =
                            candidateInterviewStatus === "availability_sent" &&
                            typeof onSelectInterviewSlot === "function";
                          const submitKey = `${selectedCandidateProfile.uid}:${index}`;
                          return (
                            <Paper
                              key={`${startsAt || "slot"}-${index}`}
                              variant="outlined"
                              sx={{
                                p: 1.25,
                                borderRadius: 1.25,
                                borderColor: isSelected ? "primary.main" : "divider",
                                bgcolor: isSelected ? "rgba(25, 118, 210, 0.05)" : "background.paper",
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                              >
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                  <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                                    <CalendarMonthIcon fontSize="small" />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                      {slot.label || `Availability slot ${index + 1}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatSlotDateTime(startsAt)}
                                    </Typography>
                                  </Box>
                                </Stack>
                                {isSelected ? (
                                  <Chip size="small" color="info" label="Selected" sx={{ borderRadius: 1 }} />
                                ) : canSelectSlot ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSelectInterviewSlot(selectedCandidateProfile, index)}
                                    disabled={Boolean(interviewSlotSubmitting)}
                                    startIcon={interviewSlotSubmitting === submitKey ? <Loader size={14} /> : <EventAvailableOutlinedIcon fontSize="small" />}
                                    sx={{ textTransform: "none" }}
                                  >
                                    {interviewSlotSubmitting === submitKey ? "Selecting..." : "Select this time"}
                                  </Button>
                                ) : null}
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Paper>
                  )}

                </Stack>
              );
            })()
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!interviewDialogCandidate}
        onClose={() => {
          if (interviewSubmitting) return;
          setInterviewDialogCandidate(null);
          setInterviewMessage("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Request interview
          <IconButton
            size="small"
            onClick={() => {
              if (interviewSubmitting) return;
              setInterviewDialogCandidate(null);
              setInterviewMessage("");
            }}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close interview request"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!!interviewDialogCandidate && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar variant="rounded" sx={{ width: 52, height: 52, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
                  <EventAvailableOutlinedIcon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {interviewDialogCandidate.displayName || "Contractor"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[formatLabel(interviewDialogCandidate.service), formatLabel(interviewDialogCandidate.level)].filter(Boolean).join(" • ") || "Candidate details"}
                  </Typography>
                </Box>
              </Stack>

              <Alert severity="info" sx={{ borderRadius: 1.25 }}>
                The contractor will be notified and can share three availability slots. Contract activation happens after selection and admin approval.
              </Alert>

              <TextField
                label="Message to contractor"
                placeholder="Share what you would like to discuss during the interview."
                value={interviewMessage}
                onChange={(event) => setInterviewMessage(event.target.value.slice(0, 500))}
                multiline
                minRows={3}
                fullWidth
                helperText={`${interviewMessage.length}/500 characters`}
                disabled={interviewSubmitting}
              />
            </Stack>
          )}
        </DialogContent>
        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={() => {
              if (interviewSubmitting) return;
              setInterviewDialogCandidate(null);
              setInterviewMessage("");
            }}
            disabled={interviewSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRequestInterview}
            disabled={interviewSubmitting}
            startIcon={interviewSubmitting ? <Loader size={14} /> : <EventAvailableOutlinedIcon />}
          >
            {interviewSubmitting ? "Sending..." : "Request interview"}
          </Button>
        </Box>
      </Dialog>

      <Dialog open={editResetConfirmOpen} onClose={() => !submittingHireRequest && setEditResetConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save changes and reset candidate activity?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Changing this request invalidates its current contractor matches, invitations, availability, and interview activity. You can find contractors again after saving.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditResetConfirmOpen(false)} disabled={submittingHireRequest}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={submittingHireRequest}
            onClick={() => { setEditResetConfirmOpen(false); handleSubmit(true); }}
          >
            Save and reset candidates
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={withdrawConfirmOpen} onClose={() => !withdrawing && setWithdrawConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Withdraw this request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This closes the request as client withdrawn. It cannot be edited or matched afterward.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawConfirmOpen(false)} disabled={withdrawing}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={withdrawing || !selectedContract}
            onClick={async () => {
              if (!selectedContract || typeof onWithdrawContract !== "function") return;
              setWithdrawing(true);
              try {
                const result = await onWithdrawContract(selectedContract.id, Number(selectedContract.version || 0));
                if (result?.ok) {
                  setWithdrawConfirmOpen(false);
                  setSelectedContract(null);
                } else {
                  setMatchNotice({ type: "error", text: result?.message || "Could not withdraw this request." });
                }
              } finally {
                setWithdrawing(false);
              }
            }}
          >
            {withdrawing ? "Withdrawing..." : "Withdraw request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hireDrawerOpen}
        onClose={closeHireDrawer}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            width: "100%",
            maxHeight: "calc(100dvh - 48px)",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderTop: "5px solid",
            borderColor: "primary.main",
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "background.paper"
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{hireMode === "edit" ? "Edit hire request" : "Request a hire"}</Typography>
              <Typography variant="body2" color="text.secondary">
                {hireMode === "edit" ? "Update the request details while it is still submitted." : "Define the role, scope, budget, and review before submitting."}
              </Typography>
            </Box>
            <IconButton edge="end" onClick={closeHireDrawer} disabled={hireSubmitting}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: "auto", flex: 1, bgcolor: "background.paper" }}>
          <Box sx={{ mx: "auto", minHeight: { md: 520 } }}>
            <Stack spacing={1.25} sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.35, textTransform: "uppercase" }}>
                  Step {hireStep + 1} of {HIRE_STEPS.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {HIRE_STEPS[hireStep]}
                </Typography>
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${HIRE_STEPS.length}, 1fr)`, gap: 0.75 }}>
                {HIRE_STEPS.map((label, index) => (
                  <Box
                    key={label}
                    sx={{
                      height: 4,
                      borderRadius: 999,
                      bgcolor: index <= hireStep ? "primary.main" : "grey.200",
                      transition: "background-color 160ms ease",
                    }}
                  />
                ))}
              </Box>
            </Stack>
          {hireStep === 0 && (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>What type of work do you need?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose the broad role family first. We will narrow the request in the next step.
                </Typography>
              </Box>

              <Stack spacing={1.25}>
                <Typography variant="body2" color="text.secondary">Role family</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.25 }}>
                  {roleFamilies.map((family) => {
                    const FamilyIcon = familyIconByCode[family.code] || EngineeringIcon;
                    return (
                      <OptionCard
                        key={family.code}
                        selected={hireForm.roleFamily === family.code}
                        onClick={() => setHireForm((prev) => ({ ...prev, roleFamily: family.code, roleCode: "", roleLevel: "" }))}
                        icon={<FamilyIcon fontSize="large" />}
                        title={family.label}
                        description={family.code === "data_ai"
                          ? "Data engineering, analytics, AI, and ML roles."
                          : "Software, platform, cloud, and product engineering roles."}
                        square={false}
                        size="regular"
                        maxWidth={680}
                        iconRailWidth={72}
                      />
                    );
                  })}
                </Box>
              </Stack>
            </Stack>
          )}

          {hireStep === 1 && (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Which specialty fits best?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select the closest specialty and level for this request.
                </Typography>
              </Box>
              <Stack spacing={1.25}>
                <Typography variant="body2" color="text.secondary">Specialty</Typography>
                {!hireForm.roleFamily ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: "grey.50" }}>
                    <Typography variant="body2" color="text.secondary">
                      Select a role family to see specialties.
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 1.25 }}>
                    {specialties.map((job) => {
                      const SpecialtyIcon = roleIconByCode[job.code] || EngineeringIcon;
                      return (
                        <OptionCard
                          key={job.code}
                          selected={hireForm.roleCode === job.code}
                          onClick={() =>
                            setHireForm((prev) => ({
                              ...prev,
                              roleCode: job.code,
                              roleLevel: job.levels?.[0]?.code || "",
                            }))
                          }
                          icon={<SpecialtyIcon fontSize="large" />}
                          title={job.label}
                          description={job.shortDescription || job.description || familyLabelByCode[job.family] || ""}
                          square={false}
                          size="regular"
                          maxWidth={680}
                          iconRailWidth={72}
                        />
                      );
                    })}
                  </Box>
                )}
                {selectedLevel ? (
                  <Typography variant="caption" color="text.secondary">
                    Starting level for pricing guidance: {selectedLevel.label || selectedLevel.code}.
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          )}

          {hireStep === 2 && (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Describe the scope</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add the outcomes, timeline, tool stack, and approval process you want covered.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Scope of work
                </Typography>
                <Suspense fallback={<Skeleton variant="rounded" height={244} />}>
                  <RichTextEditor
                    value={hireForm.requirementsHtml}
                    minHeight={180}
                    placeholder="Describe the work, outcomes, timeline, tool stack, and approval process..."
                    onChange={(html) => {
                      const sanitized = DOMPurify.sanitize(html || "");
                      const plain = toPlainText(sanitized);
                      setHireForm((prev) => ({
                        ...prev,
                        requirementsHtml: sanitized,
                        requirements: plain,
                        requirementsFormat: "html",
                      }));
                    }}
                  />
                </Suspense>
              </Box>

              <TextField
                label="Term of project (months) - optional"
                type="number"
                size="small"
                inputProps={{ min: "1", step: "1" }}
                value={hireForm.projectTermMonths}
                onChange={(e) => setHireForm((prev) => ({ ...prev, projectTermMonths: e.target.value }))}
                helperText="Leave empty if contract has no end date."
                fullWidth
              />
              <TextField
                label="Desired start date"
                type="date"
                size="small"
                value={hireForm.desiredStartDate}
                onChange={(e) => setHireForm((prev) => ({ ...prev, desiredStartDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional; leave blank if the start date is flexible."
                fullWidth
              />
            </Stack>
          )}

          {hireStep === 3 && (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>How should compensation be structured?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose hourly or fixed monthly billing and set a budget aligned with the selected level guidance.
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                <OptionCard
                  selected={hireForm.pricingModel === "hourly"}
                  onClick={() => setHireForm((prev) => ({ ...prev, pricingModel: "hourly" }))}
                  icon={<ScheduleIcon fontSize="small" />}
                  title="Hourly billing"
                  description="Set an hourly budget based on role level."
                  square={false}
                />
                <OptionCard
                  selected={hireForm.pricingModel === "fixed_monthly"}
                  onClick={() => setHireForm((prev) => ({ ...prev, pricingModel: "fixed_monthly" }))}
                  icon={<CalendarMonthIcon fontSize="small" />}
                  title="Fixed monthly"
                  description="Set a monthly fee; we show equivalent hourly."
                  square={false}
                />
              </Box>

              {hireForm.pricingModel === "hourly" ? (
                <>
                  <TextField
                    label="Hourly budget"
                    type="number"
                    size="small"
                    inputProps={{ min: String(minHourly || 0), max: String(maxHourly || 0), step: "0.01" }}
                    value={hireForm.hourlyBudget}
                    onChange={(e) => setHireForm((prev) => ({ ...prev, hourlyBudget: e.target.value }))}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">USD / hr</InputAdornment>,
                    }}
                    error={hourlyTooLow || hourlyTooHigh}
                  />
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.25, backgroundColor: (hourlyTooLow || hourlyTooHigh) ? "#fff6f6" : "grey.50", borderColor: (hourlyTooLow || hourlyTooHigh) ? "error.main" : "divider" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      Rate guidance
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Allowed hourly range: {minHourly ? formatCurrency(minHourly * 100, selectedLevel?.currency || "usd") : "—"} to {maxHourly ? formatCurrency(maxHourly * 100, selectedLevel?.currency || "usd") : "—"}/hr
                    </Typography>
                    {(hourlyTooLow || hourlyTooHigh) ? (
                      <Typography variant="caption" color="error.main" display="block">
                        Hourly budget must stay within the allowed range for the selected level.
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Suggested hourly baseline: {suggestedHourly ? `${formatCurrency(suggestedHourly * 100, selectedLevel?.currency || "usd")}/hr` : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Equivalent monthly (at 160h): {equivalentMonthlyFromHourly > 0 ? `${formatCurrency(equivalentMonthlyFromHourly * 100, selectedLevel?.currency || "usd")}/mo` : "—"}
                    </Typography>
                  </Paper>
                </>
              ) : (
                <>
                  <TextField
                    label="Fixed monthly budget"
                    type="number"
                    size="small"
                    inputProps={{ min: String(minMonthlyFromHourly || 0), max: String(maxMonthlyFromHourly || 0), step: "0.01" }}
                    value={hireForm.monthlyBudget}
                    onChange={(e) => setHireForm((prev) => ({ ...prev, monthlyBudget: e.target.value }))}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">USD / month</InputAdornment>,
                    }}
                    error={fixedMonthlyTooLow || fixedMonthlyTooHigh}
                  />
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.25, backgroundColor: (fixedMonthlyTooLow || fixedMonthlyTooHigh) ? "#fff6f6" : "grey.50", borderColor: (fixedMonthlyTooLow || fixedMonthlyTooHigh) ? "error.main" : "divider" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      Rate guidance
                    </Typography>
                    <Typography variant="caption" color={(fixedMonthlyTooLow || fixedMonthlyTooHigh) ? "error.main" : "text.secondary"} display="block">
                      Allowed monthly range: {formatCurrency(minMonthlyFromHourly * 100, selectedLevel?.currency || "usd")} to {formatCurrency(maxMonthlyFromHourly * 100, selectedLevel?.currency || "usd")}
                    </Typography>
                    {(fixedMonthlyTooLow || fixedMonthlyTooHigh) ? (
                      <Typography variant="caption" color="error.main" display="block">
                        Monthly budget must stay within the allowed range for the selected level.
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Suggested monthly baseline: {suggestedMonthly ? formatCurrency(suggestedMonthly * 100, selectedLevel?.currency || "usd") : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Equivalent hourly (at 160h): {equivalentHourlyFromMonthly > 0 ? `${formatCurrency(equivalentHourlyFromMonthly * 100, selectedLevel?.currency || "usd")}/hr` : "—"}
                    </Typography>
                  </Paper>
                </>
              )}
            </Stack>
          )}

          {hireStep === 4 && (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Does this request look correct?</Typography>
                <Typography variant="body2" color="text.secondary">
                  Review all details below before submitting. You can go back to any step to edit requirements.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Request summary
                </Typography>
                <Divider sx={{ my: 1.25 }} />
                <Stack divider={<Divider />}>
                  <Box sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Role</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(familyLabelByCode[hireForm.roleFamily] || hireForm.roleFamily || "-")} • {selectedRole?.label || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Job description</Typography>
                    {hireForm.requirementsFormat === "html" && hireForm.requirementsHtml ? (
                      <Box
                        sx={{
                          mt: 0.5,
                          p: 1.25,
                          borderRadius: 1,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          maxHeight: 200,
                          overflowY: "auto",
                          backgroundColor: "grey.50",
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(hireForm.requirementsHtml) }}
                      />
                    ) : (
                      <Typography variant="body2">{hireForm.requirements || "-"}</Typography>
                    )}
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Term</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {hireForm.projectTermMonths ? `${hireForm.projectTermMonths} months` : "Open-ended"}
                    </Typography>
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Desired start date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {hireForm.desiredStartDate ? new Date(`${hireForm.desiredStartDate}T00:00:00`).toLocaleDateString() : "Flexible"}
                    </Typography>
                  </Box>
                  <Box sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Compensation</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {hireForm.pricingModel === "hourly"
                        ? `${formatCurrency(Math.round(Number(hireForm.hourlyBudget || 0) * 100), selectedLevel?.currency || "usd")}/hr`
                        : `${formatCurrency(Math.round(Number(hireForm.monthlyBudget || 0) * 100), selectedLevel?.currency || "usd")}/mo`}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              {hireMode === "create" && <FormControlLabel
                control={
                  <Checkbox
                    checked={hireForm.acceptTerms}
                    onChange={(e) => setHireForm((prev) => ({ ...prev, acceptTerms: e.target.checked }))}
                  />
                }
                label="I confirm the request details are accurate and ready to submit."
              />}
            </Stack>
          )}
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderTop: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: "background.paper" }}>
          <Stack direction="row" justifyContent="space-between" spacing={1.25}>
            <Button
              variant="outlined"
              onClick={hireStep === 0 ? closeHireDrawer : () => setHireStep((s) => Math.max(0, s - 1))}
              disabled={hireSubmitting}
            >
              {hireStep === 0 ? "Cancel" : "Back"}
            </Button>
            {hireStep < HIRE_STEPS.length - 1 ? (
              <Button
                variant="outlined"
                onClick={() => setHireStep((s) => Math.min(HIRE_STEPS.length - 1, s + 1))}
                disabled={
                  hireSubmitting ||
                  (hireStep === 0 && !canProceedFamily) ||
                  (hireStep === 1 && !canProceedSpecialty) ||
                  (hireStep === 2 && !canProceedScope) ||
                  (hireStep === 3 && !canProceedCompensation)
                }
              >
                Next
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleSubmit}
                disabled={!canSubmitHire || hireSubmitting || (hireMode === "edit" ? typeof onUpdateContract !== "function" : typeof onSubmitPosition !== "function")}
                startIcon={hireSubmitting ? <Loader size={16} /> : null}
              >
                {hireSubmitting ? (hireMode === "edit" ? "Saving..." : "Submitting...") : (hireMode === "edit" ? "Save changes" : "Submit request")}
              </Button>
            )}
          </Stack>
        </Box>
      </Dialog>
    </Container>
  );
};

export default Contracts;
